import { Modal, Button } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropModal = ({ open, imageSrc, onCropComplete, onCancel, isMobile = false ,fileName}) => {
    const imgRef = useRef(null);
    const [crop, setCrop] = useState({
        unit: '%',
        x: 25,
        y: 25,
        width: 50,
        height: 50,
        aspect: 1, // Square crop
    });
    const [completedCrop, setCompletedCrop] = useState(null);

    // Reset crop when modal opens
    useEffect(() => {
        if (open) {
            setCrop({
                unit: '%',
                x: 25,
                y: 25,
                width: 50,
                height: 50,
                aspect: 1,
            });
            setCompletedCrop(null);
        }
    }, [open]);

  const getCroppedImg = useCallback((image, pixelCrop, fileName) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    // Set maximum dimensions (adjust these values as needed)
    const MAX_WIDTH = 800;
    const MAX_HEIGHT = 800;

    // Get the actual image dimensions
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    
    // Get the displayed image dimensions
    const displayedWidth = image.width;
    const displayedHeight = image.height;
    
    // Calculate the scale factors
    const scaleX = naturalWidth / displayedWidth;
    const scaleY = naturalHeight / displayedHeight;
    
    // Calculate the actual crop dimensions in the original image
    const actualCropX = pixelCrop.x * scaleX;
    const actualCropY = pixelCrop.y * scaleY;
    const actualCropWidth = pixelCrop.width * scaleX;
    const actualCropHeight = pixelCrop.height * scaleY;

    // Calculate final dimensions with max limits
    let finalWidth = pixelCrop.width;
    let finalHeight = pixelCrop.height;
    
    // Scale up to max dimensions if smaller
    const aspectRatio = finalWidth / finalHeight;
    
    if (finalWidth < MAX_WIDTH && finalHeight < MAX_HEIGHT) {
        if (aspectRatio >= 1) {
            // Width is larger or equal, scale based on max width
            finalWidth = Math.min(MAX_WIDTH, actualCropWidth);
            finalHeight = finalWidth / aspectRatio;
        } else {
            // Height is larger, scale based on max height
            finalHeight = Math.min(MAX_HEIGHT, actualCropHeight);
            finalWidth = finalHeight * aspectRatio;
        }
    }

    // Set canvas dimensions
    canvas.width = finalWidth;
    canvas.height = finalHeight;

    // Determine file type from fileName or use PNG as default for transparency
    const fileExtension = fileName ? fileName.split('.').pop().toLowerCase() : 'png';
    const outputFormat = fileExtension === 'png' ? 'image/png' : 'image/jpeg';
    const quality = outputFormat === 'image/jpeg' ? 0.9 : undefined;

    // For PNG files, ensure transparency is preserved
    if (outputFormat === 'image/png') {
        // Clear canvas to transparent
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Ensure transparency is preserved
        ctx.globalCompositeOperation = 'source-over';
    }

    // Draw the image
    ctx.drawImage(
        image,
        actualCropX,
        actualCropY,
        actualCropWidth,
        actualCropHeight,
        0,
        0,
        finalWidth,
        finalHeight
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                console.error('Canvas is empty');
                return;
            }
            resolve(blob);
        }, outputFormat, quality);
    });
}, []);

const handleCrop = useCallback(async () => {
        if (
            completedCrop?.width &&
            completedCrop?.height &&
            imgRef.current &&
            onCropComplete
        ) {
            try {
                const croppedImageBlob = await getCroppedImg(
                    imgRef.current,
                    completedCrop,
                    fileName // Pass the fileName here
                );
                onCropComplete(croppedImageBlob);
            } catch (error) {
                console.error('Error cropping image:', error);
            }
        }
    }, [completedCrop, getCroppedImg, onCropComplete, fileName]); 
    const handleImageLoad = useCallback((e) => {
        const { width, height } = e.currentTarget;
        
        // Calculate crop size to be 60% of the smaller dimension
        const size = Math.min(width, height) * 0.6;
        const x = (width - size) / 2;
        const y = (height - size) / 2;
        
        const pixelCrop = {
            unit: 'px',
            x,
            y,
            width: size,
            height: size,
            aspect: 1,
        };
        
        setCrop(pixelCrop);
        setCompletedCrop(pixelCrop);
    }, []);

    return (
        <Modal
            title={<span className="text-lg font-semibold">Crop Profile Photo</span>}
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            width={isMobile ? '95%' : 600}
            centered
            className="crop-modal"
            styles={{
                body: { 
                    padding: isMobile ? '16px' : '24px',
                }
            }}
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center mb-4">
                    Adjust the crop area to select the portion you want to use
                </p>
                
                {imageSrc && (
                    <div className="flex justify-center">
                        <div 
                            className="max-w-full"
                            style={{ 
                                maxHeight: isMobile ? '300px' : '400px',
                                overflow: 'hidden'
                            }}
                        >
                            <ReactCrop
                                crop={crop}
                                onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
                                onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                                aspect={1}
                                circularCrop={false}
                                keepSelection={true}
                                minWidth={50}
                                minHeight={50}
                            >
                                <img
                                    ref={imgRef}
                                    alt="Crop me"
                                    src={imageSrc}
                                    style={{ 
                                        maxWidth: '100%',
                                        maxHeight: isMobile ? '300px' : '400px',
                                        objectFit: 'contain'
                                    }}
                                    onLoad={handleImageLoad}
                                />
                            </ReactCrop>
                        </div>
                    </div>
                )}
                
                <div className="flex flex-col-reverse sm:flex-row justify-center gap-3 pt-4 border-t">
                    <Button 
                        onClick={onCancel}
                        size={isMobile ? 'middle' : 'large'}
                        className={`w-full sm:w-auto ${isMobile ? 'h-10' : 'h-12'}`}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleCrop}
                        disabled={!completedCrop?.width || !completedCrop?.height}
                        size={isMobile ? 'middle' : 'large'}
                        className={`w-full sm:w-auto ${isMobile ? 'h-10' : 'h-12'}`}
                    >
                        Crop & Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ImageCropModal;